import React, { useEffect, useState, useRef } from "react";
import AGGrid from "../../Components/AGGrid";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil } from "../../utils/CommonUtil";
import { Constants } from "../Languages/Constants";
import ToastManager from "../../Components/ToastManager";
import InputField from "../../Components/InputField";
import GenericPopup from "../../Components/GenericPopup";

const CanadianTaxRates = () => {
    const [taxRates, setTaxRates] = useState([]);
    const [history, setHistory] = useState([]);
    const [globalCadRate, setGlobalCadRate] = useState<any>("");
    const [isEditing, setIsEditing] = useState(false);
    const [tempRate, setTempRate] = useState<any>("");

    const isCadRateInvalid = (rate: any) => {
        if (!rate) return false;
        const rateStr = rate.toString();
        const parts = rateStr.split(".");
        if (parts.length !== 2) return true;
        return parts[1].length !== 4;
    };

    const gridRef = useRef<any>(null);
    const historyGridRef = useRef<any>(null);

    useEffect(() => {
        getTaxRates();
        getHistory();
    }, []);

    const getTaxRates = () => {
        NetworkManager.sendJsonRequest(
            "api/organization/canadianTaxRates",
            "",
            "",
            "GET",
            true,
            (data: any) => {
                if (data) {
                    const parsedData = JSON.parse(data);
                    setTaxRates(parsedData);
                    if (parsedData && parsedData.length > 0) {
                        setGlobalCadRate(parsedData[0].cadConversionRate);
                        setTempRate(parsedData[0].cadConversionRate);
                    }
                }
            },
            (err: any) => {
                console.error(err);
            }
        );
    };

    const getHistory = () => {
        NetworkManager.sendJsonRequest(
            "api/organization/canadianTaxRates/history",
            "",
            "",
            "GET",
            true,
            (data: any) => {
                if (data) {
                    setHistory(JSON.parse(data));
                }
            },
            (err: any) => {
                console.error(err);
            }
        );
    };

    const handleSave = () => {
        if (!tempRate) {
            ToastManager.showToast("CAD Conversion Rate is required", "Error");
            return;
        }

        const cadRateValue = parseFloat(tempRate);
        if (isNaN(cadRateValue)) {
            ToastManager.showToast("Invalid CAD Conversion Rate", "Error");
            return;
        }

        const cadRateStr = tempRate.toString();
        const parts = cadRateStr.split(".");
        if (parts.length !== 2 || parts[1].length !== 4) {
            ToastManager.showToast("CAD Conversion Rate must have exactly 4 decimal places (e.g., 1.3809)", "Error");
            return;
        }

        const body = {
            cadConversionRate: tempRate
        };

        NetworkManager.sendJsonRequest(
            "api/organization/canadianTaxRates", body, "", "PUT", true,
            (data: any) => {
                if (data) {
                    const parsedData = JSON.parse(data);
                    if (parsedData.statusCode === 200 && parsedData.message) {
                        ToastManager.showToast(CommonUtil.getMessageText(Constants.UPDATED_SUCCESSFULLY, "Updated Successfully"), "Success");
                        setGlobalCadRate(tempRate);
                        setIsEditing(false);
                        getTaxRates();
                        getHistory();
                    } else {
                        ToastManager.showToast(`${parsedData.error}, Failed to update conversion rates`, "Error");
                    }
                }
            },
            (err: any) => {
                console.error(err);
                ToastManager.showToast("Failed to update conversion rates", "Error");
            }
        );
    };

    const handleCancel = () => {
        setTempRate(globalCadRate);
        setIsEditing(false);
    };

    const columns: any = [
        { headerName: "Province", field: "province", resizable: true, sortable: true, flex: 1 },
        { headerName: "GST (%)", field: "gst", resizable: true, sortable: true, flex: 1 },
        { headerName: "PST (%)", field: "pst", resizable: true, sortable: true, flex: 1 },
        { headerName: "HST (%)", field: "hst", resizable: true, sortable: true, flex: 1 },
        { headerName: "Total Tax (%)", field: "taxTotal", resizable: true, sortable: true, flex: 1 },
    ];

    const historyColumns: any = [
        { headerName: "Old Value", field: "from", resizable: true, sortable: true, flex: 1 },
        { headerName: "New Value", field: "to", resizable: true, sortable: true, flex: 1 },
        { headerName: "Updated By", field: "changedBy", resizable: true, sortable: true, flex: 1 },
        { headerName: "Updated Date", field: "changeDate", resizable: true, sortable: true, flex: 1 },
    ];

    return (
        <>
        <div className="col-md-12 gen-panel" style={{ paddingTop: "10px" }}>
            <div className="row" style={{ padding: "10px" }}>
                <div className="col-md-12">
                    <h6>USD-CAD Conversion Rates</h6>
                </div>
            </div>

            <div className="row mb-3 mt-2 align-items-center" style={{ paddingLeft: "15px" }}>
                <div className="col-md-4 d-flex align-items-center">
                    <div style={{ flex: 1 }}>
                        <InputField
                            type="number"
                            placeholder="USD-CAD Conversion rate"
                            value={isEditing ? tempRate : globalCadRate}
                            onChange={(e: any) => setTempRate(e.target.value)}
                            onKeyPress={CommonUtil.allowNumbersDecimal}
                            disabled={!isEditing}
                        />
                        {isEditing && isCadRateInvalid(tempRate) && (
                            <small className="text-danger d-block">Exactly 4 decimal places required (e.g., 1.3293)</small>
                        )}
                    </div>
                    <div className="ms-2 d-flex align-items-center">
                        {!isEditing ? (
                            <i 
                                className="fa fa-edit" 
                                style={{ fontSize: "20px" }}
                                onClick={() => setIsEditing(true)}
                                role="button"
                                title="Edit"
                            ></i>
                        ) : (
                            <div className="d-flex align-items-center mt-2">
                            <button type="button" className="ms-2 p-1 mb-2 cursor pointer border-0 bg-transparent" role="button" title="Save" >
                                <img 
                                    src={"../../images/check.svg"} 
                                    alt="check" 
                                    width={24} 
                                    height={24} 
                                    className="cursor-pointer me-2"
                                    onClick={handleSave}
                                /></button>
                                <button type="button" className="ms-2 p-1 mb-2 cursor pointer border-0 bg-transparent"
                          role="button" title="Cancel" >
                                <img 
                                    src={"../../images/cancel.svg"} 
                                    alt="cancel" 
                                    width={24} 
                                    height={24} 
                                    className="cursor-pointer"
                                    onClick={handleCancel}
                                /></button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="ag-theme-alpine p-3 mb-4" style={{ height: "250px", width: "100%" }}>
                <AGGrid
                    id="canadianTaxRatesHistoryGrid"
                    cols={historyColumns}
                    rows={history}
                    ref={historyGridRef}
                />
            </div>
        </div>
        <div className="col-md-12 gen-panel" style={{ paddingTop: "10px" }}>
            <div className="row" style={{ padding: "10px" }}>
                <div className="col-md-12">
                    <h6>USD-CAD Tax Rates</h6>
                </div>
            </div>
            <div className="ag-theme-alpine p-3" style={{ height: "300px", width: "100%", padding: "5px" }}>
                <AGGrid
                    id="canadianTaxRatesGrid"
                    cols={columns}
                    rows={taxRates}
                    ref={gridRef}
                />
            </div>
        </div>
        </>
    );
};

export default CanadianTaxRates;
